import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
  memo,
} from 'react';
import { ResizeImageType } from '@/types';
import { getResizeImage } from '@/utils';
import Image, { ImageProps } from 'next/image';
import { mergeRefs } from 'react-merge-refs';
import { Box } from '@design-system/pc';
import styled, { css } from 'styled-components';

export type LoadImagePropsType = ImageProps &
  Omit<ResizeImageType, 'url'> & {
    className?: string;
    imgFit?: ImageProps['layout'];
    objectFit?: CSSProperties['objectFit'];
    handleChangeMeta?(args: { width: number; height: number }): void;
    skeletonSize?: { width?: string; height?: string };
    borderRadius?: React.CSSProperties['borderRadius'];
    imageScale?: number;
    isNextImage?: boolean;
  };

/**
 * 이미지가 로드가 완료된 이후에 노출
 * @author 이현우
 */
const LoadImage = React.forwardRef<HTMLImageElement, LoadImagePropsType>(
  function LoadImage(
    {
      src,
      w,
      h,
      q,
      c,
      className,
      isNextImage = false,
      imgFit = 'fill',
      objectFit,
      imageScale = 1,
      skeletonSize: _skeletonSize,
      borderRadius = 0,
      ...props
    }: LoadImagePropsType,
    ref,
  ) {
    const imageRef = useRef<HTMLImageElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const mergeRef = mergeRefs<HTMLElement>([ref, imageRef]);
    const [aspectRatio, setAspectRatio] = useState<number | null>(null);
    const [imageLoaded, setImageLoaded] = useState<boolean>(false);

    const handleChangeMeta = useCallback(() => {
      if (
        imageRef?.current?.naturalWidth != null &&
        imageRef?.current?.naturalHeight != null
      )
        props?.handleChangeMeta?.({
          width: imageRef.current.naturalWidth,
          height: imageRef.current.naturalHeight,
        });
    }, [props]);

    const updateLoadedState = useCallback(() => {
      setImageLoaded(true);
      handleChangeMeta();
    }, [handleChangeMeta]);

    const handleImageLoaded = useCallback(
      (result: { naturalWidth: number; naturalHeight: number }) => {
        if (result.naturalWidth && result.naturalHeight) {
          setAspectRatio(result.naturalWidth / result.naturalHeight);
        }
        updateLoadedState();
        props?.onLoad?.({
          ...result,
          target: imageRef.current,
        } as any);
      },
      [props, updateLoadedState],
    );

    const handleImageError: React.ReactEventHandler<HTMLImageElement> =
      useCallback(
        (e) => {
          props?.onError?.(e);
        },
        [props],
      );

    const alreadyLoadedImageUpdateState = useCallback(async () => {
      if (!imageLoaded && imageRef?.current?.complete) {
        updateLoadedState();
        props?.onLoad?.({
          status: 'alreadyLoaded',
        } as any);
      }
    }, [imageLoaded, props, updateLoadedState]);

    const calculateDimensions = useCallback(() => {
      if (!aspectRatio) return {};
      // w만 주어진 경우
      if (w && !h) {
        return { width: w, height: Math.round(w / aspectRatio) };
      }

      // h만 주어진 경우
      if (!w && h) {
        return { width: Math.round(h * aspectRatio), height: h };
      }

      // w, h 모두 주어진 경우 그대로 반환
      return { width: w, height: h };
    }, [w, h, aspectRatio]);

    const dimensions = calculateDimensions();

    const imageUrl = getResizeImage({
      url: src && typeof src === 'string' ? src : '',
      w: w ? w * imageScale : w,
      h: h ? h * imageScale : h,
      q,
      c,
    });

    // next/image의 13.0.6 이전 버전은 ref를 지원하지 않기 때문에 수동으로 ref처리
    useEffect(() => {
      if (wrapperRef.current) {
        const imgElement = wrapperRef.current.querySelector('img');
        if (imgElement) {
          mergeRef(imgElement);
        }
      }
    }, [mergeRef, src]);

    useEffect(() => {
      alreadyLoadedImageUpdateState();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <>
        {isNextImage ? (
          <Box position="relative" width="100%" height="100%">
            <Box ref={wrapperRef}>
              <Image
                {...props}
                src={imageUrl}
                width={dimensions.width}
                height={dimensions.height}
                className={className}
                objectFit={objectFit}
                layout={imgFit}
                alt={props.alt || ''}
                style={{
                  borderRadius: `${borderRadius}px`,
                }}
                onLoadingComplete={handleImageLoaded}
                onError={handleImageError}
                unoptimized
              />
            </Box>
          </Box>
        ) : (
          <Box width="100%" height="100%">
            <MemorizedStyledImage
              ref={mergeRef}
              src={imageUrl}
              alt={props.alt || ''}
              objectFit={objectFit}
              className={className}
              style={{
                borderRadius: `${borderRadius}px`,
              }}
              onError={handleImageError}
              {...props}
            />
          </Box>
        )}
      </>
    );
  },
);

export default LoadImage;

interface StyledImageProps {
  objectFit?: CSSProperties['objectFit'];
  className?: string;
  style?: React.CSSProperties;
  src?: string;
  alt?: string;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}

const StyledImage = styled.img<StyledImageProps>`
  ${({ objectFit }) => css`
    width: 100%;
    height: 100%;
    object-fit: ${objectFit || 'cover'};
  `}
`;

const MemorizedStyledImage = memo(StyledImage);
